// @workflow assistant.ui-tools
/**
 * Honest results for focus_tags and color_tags: which of the ids asked for reached the screen,
 * and why the others did not. Spatial focus reports only totals, so the host resolves the scope
 * first and hands over the subjects the server found and the ones the render plan placed;
 * this file turns those into `{ shown, skipped }`.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { ok, notAvailable, failed } from './uiToolContract.js';
import { SKIP_REASON, VIEWER_DETAIL } from './viewerContract.js';

/** `useSpatialFocus().show` statuses (useSpatialFocus.js `show` @returns). */
export const FOCUS_STATUS = Object.freeze({
  RENDERED: 'rendered',
  EMPTY: 'empty',
  UNRENDERABLE: 'unrenderable',
  UNAVAILABLE: 'unavailable',
  ABORTED: 'aborted',
  SUPERSEDED: 'superseded',
  FAILED: 'failed',
});

/** Spatial-focus subject kind for tags (aiva.api SpatialFocusContracts.cs, SpatialFocusEntityKinds.Tag). */
export const ENTITY_KIND_TAG = 'tag';

/**
 * The subject kinds the server counts as tags (aiva.api SpatialFocusResolver.cs `IsTagKind`), all
 * keyed "tag:<id>". An entity scope of kind `tag` gets its subjects minted as `tag` with the id sent.
 */
const TAG_SUBJECT_KINDS = Object.freeze([ENTITY_KIND_TAG, 'anomalytag', 'inspectiontag', 'mastertag']);

/** The facility read did not return this subject: absent, deleted or another facility's (SpatialFocusSubjectStates.Missing). */
const SUBJECT_STATE_MISSING = 'missing';

/** A render-plan claim that only greys context around a target (spatialFocusArbitrator.js STATE_CONTEXT). */
const CLAIM_STATE_CONTEXT = 'context';

const keysOf = (item) => (item && Array.isArray(item.subjectKeys) ? item.subjectKeys : []);

/**
 * The subjects a render plan puts on screen as a target: an element claim that is not context
 * only, or a marker. The same rule as useSpatialFocus.js `renderedTargetSubjects`.
 * @param {{ elementClaims?: Array<{ state: string, subjectKeys?: string[] }>, markers?: Array<{ subjectKeys?: string[] }> }|null} plan
 * @returns {string[]} sorted, unique
 */
export function placedSubjectKeys(plan) {
  const keys = new Set();
  ((plan && plan.elementClaims) || []).forEach((claim) => {
    if (claim && claim.state !== CLAIM_STATE_CONTEXT) keysOf(claim).forEach((key) => keys.add(key));
  });
  ((plan && plan.markers) || []).forEach((marker) => keysOf(marker).forEach((key) => keys.add(key)));
  return [...keys].sort();
}

/** Tag id (lowercase) → the subject keys the server found for it in this facility (missing ones left out). */
function tagSubjectKeys(subjects) {
  const byId = new Map();
  (subjects || []).forEach((subject) => {
    if (!subject || subject.id === null || subject.id === undefined || !subject.subjectKey) return;
    if (!TAG_SUBJECT_KINDS.includes(String(subject.kind).toLowerCase()) || subject.state === SUBJECT_STATE_MISSING) return;
    const id = String(subject.id).toLowerCase();
    if (!byId.has(id)) byId.set(id, []);
    byId.get(id).push(subject.subjectKey);
  });
  return byId;
}

/**
 * Each id asked for, in order, as shown or skipped: `not_found` when the server found no such tag
 * in this facility (no subject, or a "missing" one), `no_place` when it did but nothing of it was
 * placed in the loaded model. Ids come back as they were asked for.
 * @param {string[]} tagIds
 * @param {Array<{ subjectKey: string, kind: string, id: *, state?: string }>} subjects
 * @param {string[]} placedKeys
 * @returns {{ shown: string[], skipped: Array<{ id: string, reason: string }> }}
 */
export function tagOutcome(tagIds, subjects, placedKeys) {
  const byId = tagSubjectKeys(subjects);
  const placed = new Set(placedKeys || []);
  const shown = [];
  const skipped = [];
  tagIds.forEach((id) => {
    const keys = byId.get(id.toLowerCase());
    if (!keys) skipped.push({ id, reason: SKIP_REASON.NOT_FOUND });
    else if (keys.some((key) => placed.has(key))) shown.push(id);
    else skipped.push({ id, reason: SKIP_REASON.NO_PLACE });
  });
  return { shown, skipped };
}

/**
 * Something rendered, but none of it maps back to an id asked for: the subjects no longer match
 * this rule (a contract change). The scene is on screen, so this is not a failure; every id the
 * server did not report missing counts as shown, like the tool's answer before partial results.
 */
function unattributedOutcome(tagIds, subjects) {
  const missing = new Set((subjects || [])
    .filter((s) => s && s.state === SUBJECT_STATE_MISSING && TAG_SUBJECT_KINDS.includes(String(s.kind).toLowerCase()))
    .map((s) => String(s.id).toLowerCase()));
  return {
    shown: tagIds.filter((id) => !missing.has(id.toLowerCase())),
    skipped: tagIds.filter((id) => missing.has(id.toLowerCase())).map((id) => ({ id, reason: SKIP_REASON.NOT_FOUND })),
  };
}

/** None placed: "no place" when any tag was found, else "not found". */
const nothingShown = (skipped) => failed(skipped.some((s) => s.reason === SKIP_REASON.NO_PLACE) ? VIEWER_DETAIL.NO_PLACE : VIEWER_DETAIL.NOT_FOUND);

/**
 * A focus or colour outcome as a tool result: ok with `{ shown, skipped }` when at least one tag
 * is on screen, else failed (or not_available when the viewer went away) with plain words.
 * @param {{ status?: string, subjects?: Array, placedKeys?: string[] }|undefined} outcome
 * @param {string[]} tagIds
 */
export function focusOutcomeResult(outcome, tagIds) {
  const status = outcome && outcome.status;
  if (status === FOCUS_STATUS.RENDERED) {
    const result = tagOutcome(tagIds, outcome.subjects, outcome.placedKeys);
    if (result.shown.length) return ok(result);
    const placedSomething = Array.isArray(outcome.placedKeys) && outcome.placedKeys.length > 0;
    return placedSomething ? ok(unattributedOutcome(tagIds, outcome.subjects)) : nothingShown(result.skipped);
  }
  if (status === FOCUS_STATUS.UNAVAILABLE) return notAvailable(VIEWER_DETAIL.CLOSED);
  if (status === FOCUS_STATUS.EMPTY) return failed(VIEWER_DETAIL.NOT_FOUND);
  if (status === FOCUS_STATUS.UNRENDERABLE) return failed(VIEWER_DETAIL.NO_PLACE);
  if (status === FOCUS_STATUS.ABORTED || status === FOCUS_STATUS.SUPERSEDED) return failed(VIEWER_DETAIL.REPLACED);
  return failed(VIEWER_DETAIL.NOT_SHOWN);
}
